import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { ChevronUp } from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// ✅ Import images from src/assets
import homeMakerImg from "../assets/HomeCareSenior.jpg";
import personalCareImg from "../assets/PersonalCare.jpg";
import companionshipImg from "../assets/HomeCare.jpg";
import CareGiver from "../assets/CareGiver.jpg";
import IDOA from "../assets/IDoA.png";
import DuPage from "../assets/DuPage.png";
import KennethYoung from "../assets/KennethYoung.png";
import Meridian from "../assets/Meridian.png";
import IDHS from "../assets/IDHS.png";
import Molina from "../assets/Molina.png";
import BCBS from "../assets/BlueCross.png";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function HomePage() {
  const [activeTab, setActiveTab] = useState("homeMaker");
  const [showScroll, setShowScroll] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const aboutRef = useRef(null);

  // ✅ Show scroll-to-top button after scrolling 300px
  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // ✅ Auto scroll to About section when "Show More" clicked
  useEffect(() => {
    if (showMore && aboutRef.current) {
      aboutRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showMore]);

  // ✅ Pie Chart Data
  const pieData = {
    labels: ["Living in the community", "Living in an institution"],
    datasets: [
      {
        data: [82, 18],
        backgroundColor: ["#0B2B55", "#36B9CC"],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.label}: ${ctx.parsed}%` },
      },
    },
  };

  return (
    <div className="bg-gray-50">
      {/* ✅ Welcome Section */}
      <section className="text-center py-16 bg-gradient-to-b from-[#F9FAFB] to-[#E6F2F2]">
        <h1 className="text-4xl font-extrabold text-[#0B2B55] mb-6">
          Welcome to{" "}
          <span className="text-[#4FB2A7]">AllWell Companion Home Care.</span>
        </h1>
        <p className="mt-4 text-gray-700 max-w-3xl mx-auto text-lg leading-relaxed">
          At AllWell Companion Home Care LLC, we believe every individual
          deserves the opportunity to live with dignity, comfort, and
          independence — right in the place they call home. Our mission is to
          provide exceptional, compassionate, and reliable non-medical home care
          services for seniors and individuals with special needs.
        </p>
        <p className="mt-4 text-gray-700 max-w-3xl mx-auto text-lg leading-relaxed">
          From helping with daily tasks to offering companionship and emotional
          support, we go beyond basic care. Our dedicated caregivers are trained
          to deliver personalized services that are as unique as the people we
          serve — ensuring peace of mind for families and a better quality of
          life for our clients.
        </p>
        <p className="mt-4 text-gray-700 max-w-3xl mx-auto text-lg leading-relaxed">
          Proudly serving Illinois Counties including{" "}
          <strong>Cook, DuPage and Lake County</strong>, AllWell Companion Home
          Care LLC is here to make every day safer, easier, and more meaningful
          for you and your loved ones.
        </p>
      </section>

      {/* ✅ About Us Section (replaced with the fresher copy) */}
      <section
        id="about"
        ref={aboutRef}
        className="py-16 bg-gradient-to-b from-[#F7F9FA] to-white scroll-mt-40"
      >
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-extrabold text-[#0B2B55] mb-6">
            About AllWell Companion Home Care
          </h2>

          <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-6 leading-relaxed">
            AllWell Companion Home Care exists for one purpose: to help people
            stay safely and comfortably at home, on their own terms. We build
            practical, flexible care plans around each person—not around a
            template—so support fits daily routines, preferences, and goals.
          </p>

          <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-6 leading-relaxed">
            Our caregivers are selected for character first and trained for
            skill. They show up on time, listen closely, and treat every client
            like a respected partner. From medication reminders and meal prep to
            mobility support and friendly conversation, we focus on what matters
            most: confidence, safety, and a good day at home.
          </p>

          {showMore && (
            <>
              <div className="text-left max-w-2xl mx-auto mt-8">
                <h3 className="text-2xl font-bold text-[#3BA99C] mb-3">
                  What families can count on
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 text-lg">
                  <li>Respect in every interaction</li>
                  <li>Dependable schedules and clear communication</li>
                  <li>
                    Support that protects independence and privacy
                  </li>
                  <li>Relief and reassurance for loved ones</li>
                </ul>
              </div>

              <p className="text-xl font-semibold text-[#0B2B55] mt-10">
                At AllWell, care is personal. We measure success in quiet
                wins—an easier morning, a safer shower, a smile after a hard
                day—and we’re honored to earn a place in our clients’ homes and
                lives.
              </p>
            </>
          )}

          <div className="mt-8">
            <button
              onClick={() => setShowMore(!showMore)}
              className="inline-block bg-[#3BA99C] text-white font-semibold text-lg px-8 py-3 rounded-full shadow-md hover:bg-[#2E8279] transition-all duration-300 ease-in-out"
            >
              {showMore ? "Show Less" : "Show More"}
            </button>
          </div>
        </div>
      </section>

      {/* ✅ Services Tabs */}
      <section className="bg-white py-14">
        <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
          Our Services
        </h2>
        <div className="max-w-6xl mx-auto bg-white shadow-md rounded-lg border">
          {/* Tabs */}
          <div className="flex border-b bg-gray-50">
            {["homeMaker", "personalCare", "companionship"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 text-lg font-medium transition-all duration-300 ${
                  activeTab === tab
                    ? "text-[#0B2B55] border-b-2 border-[#0B2B55] bg-white"
                    : "text-gray-500 hover:text-[#008080] hover:bg-gray-100"
                }`}
              >
                {tab === "homeMaker"
                  ? "Home Support Services"
                  : tab === "personalCare"
                  ? "Personal Care Assistance"
                  : "Companionship & Social Engagement"}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex flex-col md:flex-row p-6">
            {/* LEFT SIDE - BULLETS */}
            <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-left">
              {(activeTab === "homeMaker"
                ? [
                    "Changing & Making Beds",
                    "Dusting",
                    "Vacuuming",
                    "Light Cleaning of Kitchen and Bathroom",
                    "Dish Washing",
                  ]
                : activeTab === "personalCare"
                ? [
                    "Listing Needed Supplies",
                    "Shopping",
                    "Laundry",
                    "Errands (Schedules, Appointments, Bills)",
                  ]
                : [
                    "Friendly Conversation",
                    "Playing Games or Reading",
                    "Accompanying to Appointments",
                    "Providing Emotional Support",
                  ]
              ).map((item) => (
                <div key={item} className="flex items-center space-x-2 animate-fadeIn">
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="url(#checkGradient)"
                    strokeWidth="2.3"
                    viewBox="0 0 24 24"
                  >
                    <defs>
                      <linearGradient id="checkGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#0B2B55" />
                        <stop offset="100%" stopColor="#008080" />
                      </linearGradient>
                    </defs>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 text-base">{item}</span>
                </div>
              ))}
            </div>

            {/* RIGHT SIDE - IMAGE */}
            <div className="md:w-1/3 mt-6 md:mt-0 flex justify-center">
              <img
                src={
                  activeTab === "homeMaker"
                    ? homeMakerImg
                    : activeTab === "personalCare"
                    ? personalCareImg
                    : companionshipImg
                }
                alt="Service"
                className="rounded-lg shadow-md w-full h-auto object-cover transition-all duration-500 ease-in-out"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ✅ Community Partners Carousel */}
      <section className="bg-gradient-to-r from-[#F7F9FA] to-[#E6F2F2] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-[#0B2B55] mb-10">
            Our Insurance Partners
          </h2>

        {/* If you intend to render the chart somewhere, you can use <Pie data={pieData} options={pieOptions} /> */}

          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={40}
            slidesPerView={3}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            speed={900}
            loop={true}
            breakpoints={{
              320: { slidesPerView: 1 },
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pb-20"
          >
            {[
              { img: IDOA, name: "IDOA" },
              { img: DuPage, name: "DuPage County" },
              { img: KennethYoung, name: "Kenneth Young Center" },
              { img: Meridian, name: "Meridian" },
              { img: IDHS, name: "IDHS" },
              { img: Molina, name: "Molina" },
              { img: BCBS, name: "Blue Cross Blue Shield Of Illinois" },
            ].map((partner, i) => (
              <SwiperSlide key={i}>
                <div className="flex flex-col items-center justify-between h-40">
                  <img
                    src={partner.img}
                    alt={partner.name}
                    className="mx-auto mb-1 max-h-20 object-contain"
                  />
                  <p className="text-gray-700 font-medium mt-1">{partner.name}</p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* ✅ Careers Section */}
      <section className="relative bg-[#0B2B55] text-white pt-28 pb-16">
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative max-w-7xl mx-auto flex flex-col md:flex-row items-center px-6 gap-10">
          <div className="md:w-2/3">
            <p className="text-[#3BA99C] uppercase tracking-wide font-semibold">
              Careers
            </p>
            <h2 className="text-3xl font-bold mt-2">
              Careers at AllWell Companion Home Care
            </h2>
            <p className="mt-4 text-gray-200">
              Our compassionate team is growing, and we’re looking for
              individuals who naturally find joy in caring for others.
            </p>
            <p className="mt-4 text-gray-200">
              At AllWell Companion Home Care, we are dedicated to providing
              high-quality care for those in need and we’re looking for
              passionate individuals to help us maintain this excellence.
            </p>
            <p className="mt-4 text-gray-200">
              We believe that the care industry is a rewarding career path, and
              every team member is appreciated and trained to understand the
              complexities of caregiving. If you feel our company is the right
              fit, we offer a full range of benefits—from competitive salaries
              and wages, to health insurance and paid holidays.
            </p>

            <Link
              to="/career"
              className="inline-block mt-6 px-6 py-3 bg-[#3BA99C] text-white font-semibold rounded-md shadow hover:bg-[#2E8279] transition"
            >
              APPLY NOW
            </Link>
          </div>

          <div className="md:w-1/3 flex justify-center mt-8 md:mt-0">
            <img src={CareGiver} alt="Caregiver" className="rounded-lg shadow-lg w-80" />
          </div>
        </div>
      </section>

      {/* ✅ Scroll to Top Button */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-[#0B2B55] hover:bg-[#3BA99C] text-white p-3 rounded-full shadow-lg transition-transform duration-300 hover:scale-110 z-50"
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} />
        </button>
      )}
    </div>
  );
}
